import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { parsePanelStack, serializePanelStack, type PanelEntry } from './url';

/** History state marker: this entry was pushed by `open()`, so closing can simply go back. */
interface PanelHistoryState {
  sidePanelPushed?: number;
}

export interface SidePanelApi {
  /** Open panels, bottom → top. */
  stack: PanelEntry[];
  /** Pushes a panel on top of the stack (or brings an already open one to the top). */
  open: (type: string, id: string) => void;
  /** Replaces the top panel (e.g. navigating between siblings without growing the stack). */
  replaceTop: (type: string, id: string) => void;
  /** Closes the top panel. */
  close: () => void;
  /** Closes everything above `depth` (0 = keep only the bottom panel). */
  closeTo: (depth: number) => void;
  closeAll: () => void;
  isOpen: (type: string, id: string) => boolean;
}

/**
 * URL-addressable stacked side panels ("sliders"). The stack lives in the `panel` search parameter, so panels
 * survive reloads, can be shared as links and the browser Back button closes the top panel.
 */
export function useSidePanel(): SidePanelApi {
  const location = useLocation();
  const navigate = useNavigate();
  const { search, pathname, hash } = location;
  const state = location.state as PanelHistoryState | null;
  const stack = useMemo(() => parsePanelStack(search), [search]);

  const go = useCallback(
    (next: Pick<PanelEntry, 'type' | 'id'>[], mode: 'push' | 'replace') => {
      void navigate(
        { pathname, search: serializePanelStack(search, next), hash },
        {
          replace: mode === 'replace',
          preventScrollReset: true,
          state: mode === 'push' ? { sidePanelPushed: next.length } : state,
        },
      );
    },
    [navigate, pathname, search, hash, state],
  );

  const open = useCallback(
    (type: string, id: string) => {
      const key = `${type}:${id}`;
      if (stack[stack.length - 1]?.key === key) return;
      go([...stack.filter((e) => e.key !== key), { type, id }], 'push');
    },
    [go, stack],
  );

  const replaceTop = useCallback(
    (type: string, id: string) => go([...stack.slice(0, -1), { type, id }], 'replace'),
    [go, stack],
  );

  const closeTo = useCallback(
    (depth: number) => {
      if (stack.length <= depth + 1 && depth >= 0) return;
      const next = stack.slice(0, Math.max(0, depth + 1));
      // When the top panel was opened in this session, going back restores the previous URL exactly.
      if (next.length === stack.length - 1 && state?.sidePanelPushed === stack.length) {
        void navigate(-1);
        return;
      }
      go(next, 'replace');
    },
    [go, navigate, stack, state],
  );

  const close = useCallback(() => closeTo(stack.length - 2), [closeTo, stack.length]);
  const closeAll = useCallback(() => go([], 'replace'), [go]);
  const isOpen = useCallback(
    (type: string, id: string) => stack.some((e) => e.key === `${type}:${id}`),
    [stack],
  );

  return { stack, open, replaceTop, close, closeTo, closeAll, isOpen };
}
